// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import loadCurrentCountries from "@insite/client-framework/Store/Data/Countries/Handlers/LoadCurrentCountries";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { DatePickerPresentationProps } from "@insite/mobius/DatePicker";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { Radio, RadioGroup, TextArea } from "@insite/mobius/index";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Select from "@insite/mobius/Select";
import { TextAreaPresentationProps } from '@insite/mobius/TextArea';
import TextField from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import eventRegistration from "@stanion/App/Handlers/AllEvents/EventRegistrationHandler";
import { EventRegistrationApiParameter } from "@stanion/App/Services/AllEventsService";
import { CustomAppState } from "@stanion/App/Store/Reducers";
import { xIsAuthenticated } from "@stanion/App/Store/Selectors/GeneralSelectors";
import {
    EventRegistrationState,
    initialEventRegistrationFormState,
} from "@stanion/App/Widgets/EventRegistration/EventRegistration.form.module";
import { xMask, xScreen } from "@stanion/XDL/Utilities";
import { CreateForm } from "@stanion/XDL/Utilities/form";
import cloneDeep from "lodash/cloneDeep";
import React, { useContext, useEffect, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapDispatchToProps = {
    loadCurrentCountries,
    eventRegistration,
};

const mapStateToProps = (state: ApplicationState & CustomAppState) => {
    return {
        accountSettings: getSettingsCollection(state).accountSettings,
        countries: getCurrentCountries(state),
        language: state.context.session.language,
        isAuthenticated: xIsAuthenticated(state),
    };
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CreateEventStyles {
    form?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    mainGridContainer?: GridContainerProps;
    formGridContainer?: GridContainerProps;
    fullWidthGridItem?: GridItemProps;
    submitErrorGridItem?: GridItemProps;
    submitErrorTitle?: TypographyProps;
    submitGridItem?: GridItemProps;
    submitButton?: ButtonPresentationProps;
    resetButton?: ButtonPresentationProps;
    halfWidthGridItem?: GridItemProps;
    datePickerStyles?: DatePickerPresentationProps;
    comments?: TextAreaPresentationProps;
}

export const createEventStyles: CreateEventStyles = {
    form: {
        css: css`
            margin: 0 auto;
            max-width: 600px;
            ${xScreen("lte", "sm")} {
                max-width: 100%;
            }
        `,
    },
    formGridContainer: {
        gap: 20,
    },
    fullWidthGridItem: {
        width: 12,
    },
    submitErrorGridItem: {
        width: 12,
    },
    submitErrorTitle: {
        color: "danger",
    },
    submitGridItem: {
        width: 12,
    },
    halfWidthGridItem: {
        width: 6,
        css: css`
            > div {
                width: 100%;
            }
        `,
    },
    datePickerStyles: {
        cssOverrides: {
            formField: css`
                &&& {
                    width: 100%;
                }
            `,
        },
    },
    resetButton: {
        variant: "secondary",
        css: css`
            margin-left: 20px;
        `,
    },
    comments: {
        css: css`
            resize: none;
        `,
    }
};

const styles = createEventStyles;
const StyledForm = getStyledWrapper("form");

const EventRegistration = ({ loadCurrentCountries, countries, eventRegistration, isAuthenticated, fields }: Props) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const toasterContext = useContext(ToasterContext);
    const formRef = useRef<HTMLFormElement>(null);
    const [createEventFormState, setCreateEventFormState] = useState<EventRegistrationState>(
        cloneDeep(initialEventRegistrationFormState),
    );
    const registerForm = new CreateForm<EventRegistrationState>(createEventFormState, setCreateEventFormState, {
        isAuthenticated,
    });

    useEffect(() => {
        if (!countries) {
            loadCurrentCountries();
        }
    }, []);

    const { isSubmitted } = registerForm.state;
    const formFields = registerForm?.state.fields;
    const isFormValid = registerForm.validate();

    const onSuccessHandler = () => {
        setIsSubmitting(false);
        setErrorMessage("");
        registerForm.clearState();
        toasterContext.addToast({ body: siteMessage("Custom_Event_Registration_Success"), messageType: "success" });
    };

    const submitHandler = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!isSubmitted) {
            registerForm.setSubmitted();
        }

        if (isSubmitting) {
            return;
        }

        if (!isFormValid || !formFields || !fields.url) {
            return;
        }

        setIsSubmitting(true);

        const formData: EventRegistrationApiParameter = {
            firstName: formFields.firstName.value,
            lastName: formFields.lastName.value,
            title: formFields.title.value,
            company: formFields.company.value,
            streetAddress1: formFields.streetAddress1.value,
            streetAddress2: formFields.streetAddress2.value,
            city: formFields.city.value,
            state: formFields.state.value,
            postalCode: formFields.postalCode.value,
            companyPhone: formFields.companyPhone.value,
            mobilePhone: formFields.mobilePhone.value,
            email: formFields.email.value,
            poNumber: formFields.poNumber.value,
            comments: formFields.comments.value,
            eventUrlSegment: fields.url,
            howToContact: formFields.howToContact.value,
        };

        eventRegistration({
            ...formData,
            onSuccess: onSuccessHandler,
            onError: (error: any) => {
                setIsSubmitting(false);
                setErrorMessage(error);
            },
        });
    };

    const selectedCountry = countries?.find(country => country.name === formFields.country.value);
    return (
        <StyledForm {...styles.form} onSubmit={submitHandler} noValidate ref={formRef}>
            <GridContainer {...styles.formGridContainer}>
                {!isAuthenticated && (
                    <>
                        <GridItem {...styles.halfWidthGridItem}>
                            <TextField
                                value={formFields.firstName.value}
                                onChange={e => registerForm?.onFieldChange(formFields.firstName, e.currentTarget.value)}
                                error={isSubmitted && formFields.firstName.error}
                                {...formFields.firstName.attrs}
                            />
                        </GridItem>
                        <GridItem {...styles.halfWidthGridItem}>
                            <TextField
                                value={formFields.lastName.value}
                                onChange={e => registerForm?.onFieldChange(formFields.lastName, e.currentTarget.value)}
                                error={isSubmitted && formFields.lastName.error}
                                {...formFields.lastName.attrs}
                            />
                        </GridItem>
                    </>
                )}
                <GridItem {...(isAuthenticated ? styles.fullWidthGridItem : styles.halfWidthGridItem)}>
                    <TextField
                        value={formFields.title.value}
                        onChange={e => registerForm?.onFieldChange(formFields.title, e.currentTarget.value)}
                        error={isSubmitted && formFields.title.error}
                        {...formFields.title.attrs}
                    />
                </GridItem>
                {!isAuthenticated && (
                    <>
                        <GridItem {...styles.halfWidthGridItem}>
                            <TextField
                                value={formFields.company.value}
                                onChange={e => registerForm?.onFieldChange(formFields.company, e.currentTarget.value)}
                                error={isSubmitted && formFields.company.error}
                                {...formFields.company.attrs}
                            />
                        </GridItem>
                        <GridItem {...styles.halfWidthGridItem}>
                            <TextField
                                value={formFields.streetAddress1.value}
                                onChange={e =>
                                    registerForm?.onFieldChange(formFields.streetAddress1, e.currentTarget.value)
                                }
                                error={isSubmitted && formFields.streetAddress1.error}
                                {...formFields.streetAddress1.attrs}
                            />
                        </GridItem>
                        <GridItem {...styles.halfWidthGridItem}>
                            <TextField
                                value={formFields.streetAddress2.value}
                                onChange={e =>
                                    registerForm?.onFieldChange(formFields.streetAddress2, e.currentTarget.value)
                                }
                                error={isSubmitted && formFields.streetAddress2.error}
                                {...formFields.streetAddress2.attrs}
                            />
                        </GridItem>
                        <GridItem {...styles.halfWidthGridItem}>
                            <TextField
                                value={formFields.city.value}
                                onChange={e => registerForm?.onFieldChange(formFields.city, e.currentTarget.value)}
                                error={isSubmitted && formFields.city.error}
                                {...formFields.city.attrs}
                            />
                        </GridItem>
                        <GridItem {...styles.halfWidthGridItem}>
                            <Select
                                value={formFields.country.value}
                                onChange={e => registerForm?.onFieldChange(formFields.country, e.currentTarget.value)}
                                error={isSubmitted && formFields.country.error}
                                {...formFields.country.attrs}
                            >
                                <option value="">{translate("Select Country")}</option>
                                {countries?.map(country => (
                                    <option key={country.id} value={country.name}>
                                        {country.name}
                                    </option>
                                ))}
                            </Select>
                        </GridItem>
                        <GridItem {...styles.halfWidthGridItem}>
                            <Select
                                value={formFields.state.value}
                                onChange={e => registerForm?.onFieldChange(formFields.state, e.currentTarget.value)}
                                error={isSubmitted && formFields.state.error}
                                {...formFields.state.attrs}
                            >
                                <option value="">{translate("Select State")}</option>
                                {selectedCountry?.states?.map(state => (
                                    <option key={state.id} value={state.id}>
                                        {state.name}
                                    </option>
                                ))}
                            </Select>
                        </GridItem>
                        <GridItem {...styles.halfWidthGridItem}>
                            <TextField
                                value={formFields?.email?.value}
                                onChange={e => registerForm?.onFieldChange(formFields?.email, e.currentTarget.value)}
                                error={isSubmitted && formFields?.email?.error}
                                {...formFields?.email?.attrs}
                            />
                        </GridItem>
                        <GridItem {...styles.halfWidthGridItem}>
                            <TextField
                                value={xMask.phoneNumberUS(formFields?.companyPhone?.value)}
                                onChange={e =>
                                    registerForm?.onFieldChange(formFields?.companyPhone, e.currentTarget.value)
                                }
                                error={isSubmitted && formFields?.companyPhone?.error}
                                {...formFields?.companyPhone?.attrs}
                            />
                        </GridItem>
                        <GridItem {...styles.halfWidthGridItem}>
                            <TextField
                                value={formFields?.postalCode?.value}
                                onChange={e =>
                                    registerForm?.onFieldChange(formFields?.postalCode, e.currentTarget.value)
                                }
                                error={isSubmitted && formFields?.postalCode?.error}
                                {...formFields?.postalCode?.attrs}
                            />
                        </GridItem>
                    </>
                )}

                <GridItem {...(isAuthenticated ? styles.fullWidthGridItem : styles.halfWidthGridItem)}>
                    <TextField
                        value={xMask.phoneNumberUS(formFields?.mobilePhone?.value)}
                        onChange={e => registerForm?.onFieldChange(formFields?.mobilePhone, e.currentTarget.value)}
                        error={isSubmitted && formFields?.mobilePhone?.error}
                        {...formFields?.mobilePhone?.attrs}
                    />
                </GridItem>
                <GridItem {...(isAuthenticated ? styles.fullWidthGridItem : styles.halfWidthGridItem)}>
                    <TextField
                        value={formFields?.poNumber?.value}
                        onChange={e => registerForm?.onFieldChange(formFields?.poNumber, e.currentTarget.value)}
                        error={isSubmitted && formFields?.poNumber?.error}
                        {...formFields?.poNumber?.attrs}
                    />
                </GridItem>
                <GridItem {...styles.fullWidthGridItem}>
                    <TextArea
                        value={formFields.comments.value}
                        onChange={e => registerForm?.onFieldChange(formFields.comments, e.currentTarget.value)}
                        error={isSubmitted && formFields.comments.error}
                        {...formFields.comments.attrs}
                        {...styles.comments}
                    />
                </GridItem>
                <GridItem {...styles.fullWidthGridItem}>
                    <RadioGroup
                        label={translate("How can we contact you?")}
                        value={formFields.howToContact.value}
                        onChangeHandler={e =>
                            registerForm?.onFieldChange(formFields.howToContact, e.currentTarget.value)
                        }
                    >
                        <Radio value="Email">{translate("Email")}</Radio>
                        <Radio value="Phone">{translate("Phone")}</Radio>
                        <Radio value="Additional Comments or instructions">
                            {translate("Additional Comments or instructions")}
                        </Radio>
                    </RadioGroup>
                </GridItem>
                {errorMessage && (
                    <GridItem data-test-selector="eventRegistration_ErrorMessage" {...styles.submitErrorGridItem}>
                        <Typography {...styles.submitErrorTitle}>{errorMessage}</Typography>
                    </GridItem>
                )}
                <GridItem {...styles.submitGridItem}>
                    <Button
                        {...styles.submitButton}
                        type="submit"
                        disabled={isSubmitting || (!isFormValid && isSubmitted)}
                        data-test-selector="eventRegistration_registerButton"
                    >
                        {translate("Register")}
                    </Button>
                    <Button
                        type="button"
                        {...styles.resetButton}
                        onClick={() => registerForm.clearState()}
                        data-test-selector="eventRegistration_resetButton"
                    >
                        {translate("Reset")}
                    </Button>
                </GridItem>
            </GridContainer>
        </StyledForm>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(EventRegistration),
    definition: {
        allowedContexts: ["EventPage" as any],
        group: "Event Registration" as any,
        fieldDefinitions: [
            {
                name: "url",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
